import { Suspense } from 'react'
import React from 'react'

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  GridItem,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import { groupBy } from 'src/utils/groupBy'
import { sortByDate } from 'src/utils/sort'

import { LoadingDots } from '../../../../components/LoadingDots/LoadingDots'
import {
  VideoCallHistory_ExplanationDocument,
  VideoCallHistory_ExplanationPatientVideoCallUserLogFragment,
} from '../../../../lib/gql-client'
import { useGqlQuerySWR } from '../../../../lib/gql-client/request'
import { formatDate } from '../../../../utils/formatDate'
import { getFullName } from '../../../../utils/getFullName'
import { videoCallRoomUserLogTypeToText } from '../../utils/videoCallRoomUserLogTypeToText'

type Props = {
  isOpen: boolean
  onClose: () => void
  explanationUid: string
}

export const VideoCallHistoryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  explanationUid,
}) => {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size={'lg'}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>ビデオ通話履歴</DrawerHeader>
        <DrawerBody>
          {isOpen && (
            <Suspense fallback={<LoadingDots />}>
              <VideoCallHistoryTableV2 explanationUid={explanationUid} />
            </Suspense>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

type videoCallHistory = {
  date: Date
  userLog?: VideoCallHistory_ExplanationPatientVideoCallUserLogFragment
  isSessionStarted: boolean
}

const VideoCallHistoryTableV2: React.FC<{
  explanationUid: string
}> = ({ explanationUid }) => {
  const {
    data: {
      explanation: {
        patient: {
          candidateId,
          trialPatient: {
            videoCallRoom: { userLogs },
          },
        },
        revisions: revisions,
      },
    },
  } = useGqlQuerySWR(VideoCallHistory_ExplanationDocument, {
    explanationUid: explanationUid,
  })

  // userLogsとrevisionsからallHistoriesを作成
  const allHistories: videoCallHistory[] = [
    ...userLogs
      .filter(log => log.scene !== 'ESource')
      .map(log => ({
        date: new Date(log.savedAt),
        userLog: log,
        isSessionStarted: false,
      })),
    ...revisions.flatMap(revision =>
      revision.explanationSessions.map(explanationSession => ({
        date: new Date(explanationSession.startedAt),
        userLog: undefined,
        isSessionStarted: true,
      })),
    ),
  ]

  const sortedAllHistories = sortByDate(allHistories, 'date', 'desc')

  // ログを日付ごとにまとめる
  const logsByDate = groupBy(sortedAllHistories, log =>
    formatDate(log.date, 'YYYY/MM/DD'),
  )

  return (
    <Stack spacing={4}>
      {Object.entries(logsByDate).map(([date, logs]) => {
        // ビデオ通話のログがない場合は表示しない
        if (logs.filter(log => log.isSessionStarted).length === logs.length) {
          return null
        }
        return (
          <Stack key={date} spacing={2} marginRight={20}>
            <Box px="4" py="2" bg="blue.50">
              <Text>{formatDate(date, 'YYYY/MM/DD (ddd)')}</Text>
            </Box>
            <Grid
              templateColumns="repeat(3, 1fr)"
              gap={4}
              padding={4}
              marginRight={10}
            >
              {logs.reverse().map(log => {
                if (log.isSessionStarted) {
                  return (
                    <GridItem
                      colSpan={3}
                      key={log.date.toString()}
                      fontSize="sm"
                      marginLeft={5}
                    >
                      <HStack gap={10}>
                        <Text>⚪︎説明開始</Text>
                        <Text>{formatDate(log.date, 'HH:mm')}</Text>
                      </HStack>
                    </GridItem>
                  )
                }
                if (!log.userLog) {
                  return null
                }
                return (
                  <>
                    <GridItem marginLeft={10} width="200px">
                      {log.userLog.trialMember ? (
                        <MemberLabel
                          role={log.userLog.trialMember.role}
                          displayName={getFullName(
                            log.userLog.trialMember.user,
                          )}
                          isPartner={
                            // ビデオ通話履歴にtrialHospitalUidがないため、便宜的に1つでもMainがあればMainとして扱う
                            log.userLog.trialMember.trialHospitals.filter(
                              t => t.role === 'Main',
                            ).length === 0
                          }
                        />
                      ) : (
                        <Text>患者 候補ID: {candidateId}</Text>
                      )}
                    </GridItem>
                    <GridItem>
                      {!!log.date && formatDate(log.date, 'HH:mm')}
                    </GridItem>
                    <GridItem>
                      {videoCallRoomUserLogTypeToText(
                        log.userLog.videoCallRoomLogType,
                      )}
                    </GridItem>
                  </>
                )
              })}
            </Grid>
          </Stack>
        )
      })}
    </Stack>
  )
}
